import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RefreshCw, Bomb, Zap, ChevronRight, User, TrendingUp, Activity, X } from 'lucide-react';
import { useBetaCredits } from '@/hooks/useBetaCredits';
import MatchupBoxScore, { type BoxScorePlayer } from '@/components/mlb-beta/MatchupBoxScore';
import { toast } from 'sonner';
import { logErrorToast } from '@/lib/errorLogger';
import { cn } from '@/lib/utils';
import { resolveDefaultWeek, weekLabel } from '@/lib/playoffWeeks';
import SabotageWheel from '@/components/SabotageWheel';
import { MLB_MATCHUP_SABOTAGES } from '@/data/sabotageCatalog';
import { RARITY_CONFIG } from '@/types/sabotageStats';
import { useSabotageEngine } from '@/hooks/useSabotageEngine';
import { MLB_SABOTAGE_OPTIONS } from '@/types/league';
import type { SabotageOption } from '@/types/league';
import type { SabotageCatalogEntry } from '@/types/sabotageStats';
import WeeklyRecap from './WeeklyRecap';
import { buildScoringWeights, DEFAULT_TRASH_WEIGHTS } from '@/lib/trashScoring';
import { MLB_TEAM_ID_TO_ABBR } from '@/data/mlbTeams';
import { getTodayET } from '@/hooks/useAddDrop';

interface MatchupRow {
  id: string;
  week: number;
  home_team: string;
  away_team: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  status: string;
  week_start: string;
  week_end: string;
}

interface Standing {
  team_id: string;
  team_name: string;
  wins: number;
  losses: number;
  ties: number;
  total_points: number;
}

interface PlayerStat {
  player_id: string;
  player_name: string;
  position: string;
  team_name: string;
  trash_points: number;
  is_starter: boolean;
  slot: string;
  stats: Record<string, number>;
  prev_points?: number;
  headshot_url?: string | null;
  upcoming_games?: { date: string; opponent: string; isHome: boolean }[];
}

const REFRESH_INTERVAL = 15_000;
const LIVE_DETAIL_REFRESH = 15_000;
const ALL_STAT_KEYS = ['strikeouts', 'errors', 'gidp', 'caught_stealing', 'earned_runs', 'walks_allowed', 'wild_pitches', 'blown_saves', 'hit_batsmen', 'home_runs_allowed', 'hits_allowed', 'balks', 'passed_balls', 'home_runs', 'hits', 'rbi', 'runs_scored', 'stolen_bases', 'doubles', 'triples', 'walks_drawn', 'pitcher_strikeouts', 'pitcher_wins', 'pitcher_losses', 'saves', 'holds', 'outfield_assists', 'left_on_base', 'hit_by_pitch', 'at_bats', 'innings_pitched', 'throwing_errors'];
const GAME_STATS_SELECT = 'player_id, strikeouts, errors, gidp, caught_stealing, earned_runs, walks_allowed, wild_pitches, blown_saves, hit_batsmen, home_runs_allowed, hits_allowed, balks, passed_balls, home_runs, hits, rbi, runs_scored, stolen_bases, doubles, triples, walks_drawn, pitcher_strikeouts, pitcher_wins, pitcher_losses, saves, holds, outfield_assists, lob, hit_by_pitch, at_bats, innings_pitched, throwing_errors';

/** Map a SabotageOption (wheel result) to a validated catalog entry */
function mapWheelResultToCatalog(option: SabotageOption): SabotageCatalogEntry | null {
  // Match by name (case-insensitive)
  const match = MLB_MATCHUP_SABOTAGES.find(
    s => s.name.toLowerCase() === option.name.toLowerCase()
  );
  return match || null;
}

const MLBBetaMatchups = () => {
  const [matchups, setMatchups] = useState<MatchupRow[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [maxWeek, setMaxWeek] = useState(1);
  const [regularSeasonWeeks, setRegularSeasonWeeks] = useState(0);
  const [playoffTeams, setPlayoffTeams] = useState(0);
  const [leagueId, setLeagueId] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const leagueIdRef = useRef<string | null>(null);

  // Sabotage wheel state
  const [wheelOpen, setWheelOpen] = useState(false);
  const [wheelMatchup, setWheelMatchup] = useState<MatchupRow | null>(null);
  const [wheelTargetId, setWheelTargetId] = useState('');
  const [spinResult, setSpinResult] = useState<SabotageOption | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Matchup detail state
  const [detailMatchup, setDetailMatchup] = useState<MatchupRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [homePlayers, setHomePlayers] = useState<PlayerStat[]>([]);
  const [awayPlayers, setAwayPlayers] = useState<PlayerStat[]>([]);
  const [prevHomeScore, setPrevHomeScore] = useState<number | null>(null);
  const [prevAwayScore, setPrevAwayScore] = useState<number | null>(null);
  const [scoreFlash, setScoreFlash] = useState<'home' | 'away' | null>(null);
  const detailRefreshRef = useRef<NodeJS.Timeout | null>(null);

  const { sabotages, applySabotage, fetchSabotages } = useSabotageEngine(leagueId);
  const betaCredits = useBetaCredits();
  const [userTeamId, setUserTeamId] = useState<string | null>(null);
  const [spinCost, setSpinCost] = useState(10);
  const [maxSpinsPerMatchup, setMaxSpinsPerMatchup] = useState<number | null>(null);
  const [scoringWeights, setScoringWeights] = useState<Record<string, number>>(DEFAULT_TRASH_WEIGHTS);
  const [lineupLockType, setLineupLockType] = useState<string>('weekly');
  // Confirm flow: after wheel lands, show the result with confirm/cancel
  const [pendingSabotage, setPendingSabotage] = useState<SabotageOption | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  // Probable pitcher player IDs for today (used in matchup box score)
  const [ppPlayerIds, setPpPlayerIds] = useState<Set<string>>(new Set());
  // Postponed game player IDs for today
  const [ppdPlayerIds, setPpdPlayerIds] = useState<Set<string>>(new Set());
  // Time period toggle for matchup detail view
  const [matchupTimePeriod, setMatchupTimePeriod] = useState<'week' | 'today'>('week');
  const matchupTimePeriodRef = useRef<'week' | 'today'>('week');

  const fetchMatchupDetail = useCallback(async (matchup: MatchupRow, isRefresh = false) => {
    if (!isRefresh) {
      trackEvent('matchup_viewed', { matchup_id: matchup.id, week: matchup.week, opponent_name: matchup.away_team });
      setDetailMatchup(matchup);
      setDetailLoading(true);
      setHomePlayers([]);
      setAwayPlayers([]);
      setPrevHomeScore(null);
      setPrevAwayScore(null);
    }

    try {
      // Fetch rosters for both teams
      const [homeRoster, awayRoster] = await Promise.all([
        supabase.from('roster_assignments')
          .select('player_id, is_starter, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
          .eq('team_id', matchup.home_team_id),
        supabase.from('roster_assignments')
          .select('player_id, is_starter, slot, players!inner(full_name, position_abbreviation, mlb_team_abbreviation, headshot_url)')
          .eq('team_id', matchup.away_team_id),
      ]);
      if (homeRoster.error) console.error('[Matchups] home roster error:', homeRoster.error.message);
      if (awayRoster.error) console.error('[Matchups] away roster error:', awayRoster.error.message);

      const homeRosterIds = new Set((homeRoster.data || []).map((r: any) => r.player_id));
      const awayRosterIds = new Set((awayRoster.data || []).map((r: any) => r.player_id));
      const allPlayerIds = [...homeRosterIds, ...awayRosterIds];

      // Read pre-computed scores from player_match_scores (single source of truth)
      const scoresByPlayer: Record<string, { trash_points: number; anti_points: number; total_points: number; stats: Record<string, number> }> = {};
      // Track which team_id each scored player belongs to (for dropped player attribution)
      const pmsTeamMap: Record<string, string> = {};
      const pmsStarterMap: Record<string, boolean> = {};
      if (matchup.status !== 'scheduled' && leagueIdRef.current) {
        const { data: pmsData, error: pmsError } = await supabase
          .from('player_match_scores')
          .select('player_id, team_id, is_starter, trash_points, anti_points, total_points')
          .eq('matchup_id', matchup.id);
        if (pmsError) console.error('[Matchups] player_match_scores error:', pmsError.message);

        for (const row of (pmsData || [])) {
          const pid = row.player_id;
          if (!scoresByPlayer[pid]) {
            scoresByPlayer[pid] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
          }
          scoresByPlayer[pid].trash_points += Number(row.trash_points);
          scoresByPlayer[pid].anti_points += Number(row.anti_points);
          scoresByPlayer[pid].total_points += Number(row.total_points);
          // Remember team assignment and starter status from when scores were written
          if (row.team_id) pmsTeamMap[pid] = row.team_id;
          if (row.is_starter) pmsStarterMap[pid] = true;
        }

        // Find dropped players: have pms rows for this matchup but not on current roster
        const droppedFromPms = Object.keys(scoresByPlayer).filter(
          pid => !homeRosterIds.has(pid) && !awayRosterIds.has(pid) && pmsTeamMap[pid]
        );

        // Also check roster_transactions for drops during the matchup week
        // (catches players whose pms rows haven't been backfilled yet)
        const dropTypes = ['drop', 'drop_only', 'trade_out', 'waiver_drop', 'commissioner_drop'];
        const { data: txnDrops } = await supabase
          .from('roster_transactions' as any)
          .select('player_id, team_id')
          .in('team_id', [matchup.home_team_id, matchup.away_team_id])
          .in('transaction_type', dropTypes)
          .gte('executed_at', matchup.week_start)
          .lte('executed_at', matchup.week_end + 'T23:59:59Z');

        // Build map of dropped player → team from transactions
        const txnTeamMap = new Map<string, string>();
        for (const d of (txnDrops as any[] || [])) {
          if (!homeRosterIds.has(d.player_id) && !awayRosterIds.has(d.player_id)) {
            txnTeamMap.set(d.player_id, d.team_id);
          }
        }

        // Merge both sources: pms-detected drops + transaction-detected drops
        const allDroppedIds = new Set([...droppedFromPms, ...txnTeamMap.keys()]);

        // For dropped players not already in scoresByPlayer, fetch their scores
        // by team_id + date range (covers cases where matchup_id was cleared by engine re-run)
        const missingDrops = [...allDroppedIds].filter(pid => !scoresByPlayer[pid]);
        if (missingDrops.length > 0) {
          // Try fetching scores attributed to the team (team_id set)
          const { data: teamScores } = await supabase
            .from('player_match_scores')
            .select('player_id, team_id, trash_points, anti_points, total_points, is_starter')
            .in('team_id', [matchup.home_team_id, matchup.away_team_id])
            .in('player_id', missingDrops)
            .gte('game_date', matchup.week_start)
            .lte('game_date', matchup.week_end);

          for (const row of (teamScores || [])) {
            const pid = row.player_id;
            if (!scoresByPlayer[pid]) {
              scoresByPlayer[pid] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
            }
            scoresByPlayer[pid].trash_points += Number(row.trash_points);
            scoresByPlayer[pid].anti_points += Number(row.anti_points);
            scoresByPlayer[pid].total_points += Number(row.total_points);
            if (row.team_id) pmsTeamMap[pid] = row.team_id;
            if (row.is_starter) pmsStarterMap[pid] = true;
          }

          // Also check for unrostered scores (team_id IS NULL) as fallback
          const stillMissing = missingDrops.filter(pid => !scoresByPlayer[pid]);
          if (stillMissing.length > 0) {
            const { data: unrosteredScores } = await supabase
              .from('player_match_scores')
              .select('player_id, trash_points, anti_points, total_points, is_starter')
              .eq('league_id', leagueIdRef.current)
              .is('team_id', null)
              .in('player_id', stillMissing)
              .gte('game_date', matchup.week_start)
              .lte('game_date', matchup.week_end);

            for (const row of (unrosteredScores || [])) {
              const pid = row.player_id;
              if (!scoresByPlayer[pid]) {
                scoresByPlayer[pid] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
              }
              scoresByPlayer[pid].trash_points += Number(row.trash_points);
              scoresByPlayer[pid].anti_points += Number(row.anti_points);
              scoresByPlayer[pid].total_points += Number(row.total_points);
              if (row.is_starter) pmsStarterMap[pid] = true;
            }
          }
        }

        // Fetch player info for all dropped players and add to appropriate roster
        const droppedPlayerIds = [...allDroppedIds];
        if (droppedPlayerIds.length > 0) {
          const { data: droppedInfo } = await supabase
            .from('players')
            .select('id, full_name, position_abbreviation, mlb_team_abbreviation, headshot_url')
            .in('id', droppedPlayerIds);

          for (const p of (droppedInfo || [])) {
            // Determine team: prefer pms team_id, fall back to transaction team_id
            const teamId = pmsTeamMap[p.id] || txnTeamMap.get(p.id);
            if (!teamId) continue;
            const fakeRosterRow = {
              player_id: p.id,
              is_starter: pmsStarterMap[p.id] || false,
              slot: 'DROPPED',
              players: {
                full_name: p.full_name,
                position_abbreviation: p.position_abbreviation,
                mlb_team_abbreviation: p.mlb_team_abbreviation,
                headshot_url: p.headshot_url,
              },
            };
            // Attribute to the team they were on
            if (teamId === matchup.home_team_id) {
              (homeRoster.data as any[])?.push(fakeRosterRow);
            } else if (teamId === matchup.away_team_id) {
              (awayRoster.data as any[])?.push(fakeRosterRow);
            }
          }
          // Update allPlayerIds to include dropped players for game stats fetch
          allPlayerIds.push(...droppedPlayerIds);
        }

        // Fetch raw game stats for box score breakdown display
        const { data: gameStats, error: statsError } = await supabase
          .from('player_game_stats')
          .select(GAME_STATS_SELECT)
          .in('player_id', allPlayerIds)
          .gte('game_date', matchup.week_start)
          .lte('game_date', matchup.week_end);
        if (statsError) console.error('[Matchups] game stats error:', statsError.message);

        for (const gs of (gameStats || [])) {
          if (!scoresByPlayer[gs.player_id]) {
            scoresByPlayer[gs.player_id] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
          }
          const entry = scoresByPlayer[gs.player_id];
          for (const key of ALL_STAT_KEYS) {
            const srcKey = key === 'left_on_base' ? 'lob' : key;
            entry.stats[key] = (entry.stats[key] || 0) + Number((gs as any)[srcKey] || 0);
          }
        }
      }

      // Override scores for today/season time period
      const timePeriod = matchupTimePeriodRef.current;
      if (timePeriod === 'today') {
        for (const k of Object.keys(scoresByPlayer)) delete scoresByPlayer[k];
        const todayET = getTodayET();
        if (leagueIdRef.current) {
          const { data: todayPms } = await supabase
            .from('player_match_scores')
            .select('player_id, team_id, is_starter, trash_points, anti_points, total_points')
            .eq('league_id', leagueIdRef.current)
            .eq('game_date', todayET)
            .in('team_id', [matchup.home_team_id, matchup.away_team_id]);
          for (const row of (todayPms || [])) {
            if (!scoresByPlayer[row.player_id]) {
              scoresByPlayer[row.player_id] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
            }
            scoresByPlayer[row.player_id].total_points += Number(row.total_points);
          }
        }
        const { data: todayStats } = await supabase
          .from('player_game_stats')
          .select(GAME_STATS_SELECT)
          .in('player_id', allPlayerIds)
          .eq('game_date', todayET);
        for (const gs of (todayStats || [])) {
          if (!scoresByPlayer[gs.player_id]) {
            scoresByPlayer[gs.player_id] = { trash_points: 0, anti_points: 0, total_points: 0, stats: {} };
          }
          const entry = scoresByPlayer[gs.player_id];
          for (const key of ALL_STAT_KEYS) {
            const srcKey = key === 'left_on_base' ? 'lob' : key;
            entry.stats[key] = (entry.stats[key] || 0) + Number((gs as any)[srcKey] || 0);
          }
        }
      }

      // Fetch upcoming game schedule for the matchup period (future dates only)
      const today = getTodayET();
      const upcomingByAbbr = new Map<string, { date: string; opponent: string; isHome: boolean }[]>();
      if (matchup.week_end >= today) {
        const scheduleStart = today < matchup.week_start ? matchup.week_start : today;
        const { data: schedData } = await supabase
          .from('game_schedule')
          .select('game_date, home_team_id, away_team_id')
          .gte('game_date', scheduleStart)
          .lte('game_date', matchup.week_end)
          .order('game_date');

        for (const g of (schedData || [])) {
          const homeAbbr = MLB_TEAM_ID_TO_ABBR[g.home_team_id];
          const awayAbbr = MLB_TEAM_ID_TO_ABBR[g.away_team_id];
          if (homeAbbr) {
            if (!upcomingByAbbr.has(homeAbbr)) upcomingByAbbr.set(homeAbbr, []);
            upcomingByAbbr.get(homeAbbr)!.push({ date: g.game_date, opponent: awayAbbr || '??', isHome: true });
          }
          if (awayAbbr) {
            if (!upcomingByAbbr.has(awayAbbr)) upcomingByAbbr.set(awayAbbr, []);
            upcomingByAbbr.get(awayAbbr)!.push({ date: g.game_date, opponent: homeAbbr || '??', isHome: false });
          }
        }
      }

      // Fetch today's game data for PP badge and PPD badge in box score
      const todayStr = getTodayET();
      const { data: todayGames } = await supabase
        .from('game_schedule')
        .select('home_team_id, away_team_id, status, home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .eq('game_date', todayStr);
      const ppMlbIds = new Set<number>();
      for (const g of (todayGames || [])) {
        if (g.home_probable_pitcher_mlb_id) ppMlbIds.add(g.home_probable_pitcher_mlb_id);
        if (g.away_probable_pitcher_mlb_id) ppMlbIds.add(g.away_probable_pitcher_mlb_id);
      }
      // Map to player UUIDs using the roster data
      if (ppMlbIds.size > 0) {
        const allRosterPlayerIds = [...new Set([
          ...(homeRoster.data || []).map((r: any) => r.player_id),
          ...(awayRoster.data || []).map((r: any) => r.player_id),
        ])];
        if (allRosterPlayerIds.length > 0) {
          const { data: playerMlbIds } = await supabase
            .from('players')
            .select('id, mlb_id')
            .in('id', allRosterPlayerIds)
            .not('mlb_id', 'is', null);
          const newPpIds = new Set<string>();
          for (const p of (playerMlbIds || [])) {
            if (p.mlb_id && ppMlbIds.has(p.mlb_id)) newPpIds.add(p.id);
          }
          setPpPlayerIds(newPpIds);
        }
      } else {
        setPpPlayerIds(new Set());
      }

      // Identify players on teams with postponed games today
      const ppdTeamIds = new Set<number>();
      for (const g of (todayGames || [])) {
        if (g.status === 'postponed') {
          ppdTeamIds.add(g.home_team_id);
          ppdTeamIds.add(g.away_team_id);
        }
      }
      if (ppdTeamIds.size > 0) {
        const ppdAbbrs = new Set<string>();
        for (const tid of ppdTeamIds) {
          const abbr = MLB_TEAM_ID_TO_ABBR[tid];
          if (abbr) ppdAbbrs.add(abbr);
        }
        // Map roster players to PPD set by matching their mlb_team_abbreviation
        const allRosterIds = [...new Set([
          ...(homeRoster.data || []).map((r: any) => r.player_id),
          ...(awayRoster.data || []).map((r: any) => r.player_id),
        ])];
        if (allRosterIds.length > 0) {
          const { data: rosterPlayers } = await supabase
            .from('players')
            .select('id, mlb_team_abbreviation')
            .in('id', allRosterIds);
          const newPpdIds = new Set<string>();
          for (const p of (rosterPlayers || [])) {
            if (p.mlb_team_abbreviation && ppdAbbrs.has(p.mlb_team_abbreviation)) {
              newPpdIds.add(p.id);
            }
          }
          setPpdPlayerIds(newPpdIds);
        }
      } else {
        setPpdPlayerIds(new Set());
      }

      const mapRoster = (roster: any[], prevPlayers: PlayerStat[]): PlayerStat[] =>
        roster.map((r: any) => {
          const scores = scoresByPlayer[r.player_id];
          const points = scores?.total_points || 0;
          const prev = prevPlayers.find(p => p.player_id === r.player_id);
          const teamAbbr = (r.players as any)?.mlb_team_abbreviation || '';
          return {
            player_id: r.player_id,
            player_name: (r.players as any)?.full_name || 'Unknown',
            position: (r.players as any)?.position_abbreviation || '??',
            team_name: teamAbbr,
            trash_points: points,
            is_starter: r.is_starter,
            slot: r.slot || 'BENCH',
            stats: scores?.stats || {},
            prev_points: prev ? prev.trash_points : undefined,
            headshot_url: (r.players as any)?.headshot_url || null,
            upcoming_games: upcomingByAbbr.get(teamAbbr) || [],
          };
        })
        .sort((a, b) => b.is_starter === a.is_starter ? b.trash_points - a.trash_points : (b.is_starter ? 1 : -1) - (a.is_starter ? 1 : -1));

      setHomePlayers(prev => mapRoster(homeRoster.data || [], prev));
      setAwayPlayers(prev => mapRoster(awayRoster.data || [], prev));

      // Also refresh the matchup score itself
      if (isRefresh) {
        const { data: freshMatchup } = await supabase
          .from('matchups')
          .select('home_score, away_score, status')
          .eq('id', matchup.id)
          .single();

        if (freshMatchup) {
          const newHome = Number(freshMatchup.home_score);
          const newAway = Number(freshMatchup.away_score);

          setDetailMatchup(prev => prev ? {
            ...prev,
            home_score: newHome,
            away_score: newAway,
            status: freshMatchup.status,
          } : prev);

          // Flash animation on score change
          if (prevHomeScore !== null && newHome !== prevHomeScore) {
            setScoreFlash('home');
            setTimeout(() => setScoreFlash(null), 1500);
          }
          if (prevAwayScore !== null && newAway !== prevAwayScore) {
            setScoreFlash('away');
            setTimeout(() => setScoreFlash(null), 1500);
          }

          setPrevHomeScore(newHome);
          setPrevAwayScore(newAway);
        }
      } else {
        setPrevHomeScore(matchup.home_score);
        setPrevAwayScore(matchup.away_score);
      }
    } catch (err) {
      console.error('[Matchups] fetchMatchupDetail exception:', err);
    } finally {
      setDetailLoading(false);
    }
  }, [prevHomeScore, prevAwayScore, scoringWeights]);

  // Auto-refresh matchup detail every 15s for live matchups
  useEffect(() => {
    if (detailRefreshRef.current) {
      clearInterval(detailRefreshRef.current);
      detailRefreshRef.current = null;
    }

    if (detailMatchup && detailMatchup.status === 'live') {
      detailRefreshRef.current = setInterval(() => {
        fetchMatchupDetail(detailMatchup, true);
      }, LIVE_DETAIL_REFRESH);
    }

    return () => {
      if (detailRefreshRef.current) {
        clearInterval(detailRefreshRef.current);
      }
    };
  }, [detailMatchup?.id, detailMatchup?.status, fetchMatchupDetail]);

  // Keep period ref in sync and re-fetch when period changes
  useEffect(() => {
    matchupTimePeriodRef.current = matchupTimePeriod;
    if (detailMatchup) {
      fetchMatchupDetail(detailMatchup, true);
    }
  }, [matchupTimePeriod]);

  // Reset period when dialog closes
  useEffect(() => {
    if (!detailMatchup) setMatchupTimePeriod('week');
  }, [detailMatchup]);

  // Compute filtered scores from player data for non-week views
  const filteredHomeScore = useMemo(() => {
    if (matchupTimePeriod === 'week') return detailMatchup?.home_score ?? 0;
    return Math.round(homePlayers.reduce((sum, p) => sum + p.trash_points, 0) * 10) / 10;
  }, [matchupTimePeriod, homePlayers, detailMatchup?.home_score]);

  const filteredAwayScore = useMemo(() => {
    if (matchupTimePeriod === 'week') return detailMatchup?.away_score ?? 0;
    return Math.round(awayPlayers.reduce((sum, p) => sum + p.trash_points, 0) * 10) / 10;
  }, [matchupTimePeriod, awayPlayers, detailMatchup?.away_score]);

  const processMatchups = useCallback((allMatchups: any[]) => {
    const mapped = allMatchups.map((m: any) => ({
      id: m.id,
      week: m.week,
      home_team: m.home_team_name || 'Unknown',
      away_team: m.away_team_name || 'Unknown',
      home_team_id: m.home_team_id,
      away_team_id: m.away_team_id,
      home_score: Number(m.home_score),
      away_score: Number(m.away_score),
      status: m.status,
      week_start: m.week_start,
      week_end: m.week_end,
    }));

    setMatchups(mapped);

    const maxW = Math.max(...mapped.map(m => m.week), 1);
    setMaxWeek(maxW);
    // live -> first scheduled -> last final -> 1, shared with MLBBetaFantasySchedule.
    // Accepting only 'live' opened this tab on Week 1 whenever no week was live, which is
    // every between-rounds gap, every quiet-hours window, and every completed season.
    const defaultWeek = resolveDefaultWeek(mapped);
    setCurrentWeek(prev => (prev === 1 ? defaultWeek : prev));

    // Compute standings
    const records: Record<string, { name: string; wins: number; losses: number; ties: number; total: number }> = {};
    for (const m of mapped) {
      if (!records[m.home_team_id]) records[m.home_team_id] = { name: m.home_team, wins: 0, losses: 0, ties: 0, total: 0 };
      if (!records[m.away_team_id]) records[m.away_team_id] = { name: m.away_team, wins: 0, losses: 0, ties: 0, total: 0 };
      records[m.home_team_id].total += m.home_score;
      records[m.away_team_id].total += m.away_score;
      if (m.status === 'final' || m.status === 'live') {
        if (m.home_score > m.away_score) {
          records[m.home_team_id].wins++;
          records[m.away_team_id].losses++;
        } else if (m.away_score > m.home_score) {
          records[m.away_team_id].wins++;
          records[m.home_team_id].losses++;
        } else if (m.home_score > 0) {
          records[m.home_team_id].ties++;
          records[m.away_team_id].ties++;
        }
      }
    }

    const standingsData = Object.entries(records).map(([id, r]) => ({
      team_id: id,
      team_name: r.name,
      wins: r.wins,
      losses: r.losses,
      ties: r.ties,
      total_points: Math.round(r.total * 10) / 10,
    })).sort((a, b) => b.wins - a.wins || b.total_points - a.total_points);

    setStandings(standingsData);
    setLastUpdated(new Date());
  }, []);

  const fetchData = useCallback(async (lgId?: string) => {
    try {
      const id = lgId || leagueIdRef.current;
      if (!id) return;

      const { data: allMatchups, error } = await supabase
        .from('matchups')
        .select('id, week, home_team_id, away_team_id, home_team_name, away_team_name, home_score, away_score, status, week_start, week_end')
        .eq('league_id', id)
        .order('week');

      if (error) { console.error('[Matchups] fetchData error:', error.message); return; }
      if (allMatchups) processMatchups(allMatchups);
    } catch (err) {
      console.error('[Matchups] fetchData exception:', err);
    }
  }, [processMatchups]);

  useEffect(() => {
    const init = async () => {
      try {
        // Use getSession() instead of getUser() — getUser() makes a network call
        // that throws TypeError: Load failed on iOS/Capacitor when session isn't ready
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('[Matchups] getSession error:', sessionError.message);
          setLoading(false);
          return;
        }
        const user = sessionData?.session?.user;
        if (!user) {
          console.error('[Matchups] no user session');
          setLoading(false);
          return;
        }

        // First check leagues where user is commissioner
        const { data: ownedLeague, error: ownedError } = await supabase
          .from('leagues')
          .select('id')
          .eq('commissioner_id', user.id)
          .eq('sport', 'mlb')
          .limit(1)
          .maybeSingle();
        if (ownedError) console.error('[Matchups] owned league query error:', ownedError.message);

        let foundId = ownedLeague?.id;

        // If not commissioner, check league membership
        if (!foundId) {
          const { data: membership, error: memberError } = await supabase
            .from('league_members')
            .select('league_id, leagues!inner(id, sport)')
            .eq('user_id', user.id)
            .limit(1);
          if (memberError) console.error('[Matchups] membership query error:', memberError.message);

          if (membership && membership.length > 0) {
            const mlbMember = membership.find((m: any) => (m as any).leagues?.sport === 'mlb');
            if (mlbMember) foundId = mlbMember.league_id;
          }
        }

        // Fallback: look for the seeded test league
        if (!foundId) {
          const { data: testLeague, error: testError } = await supabase
            .from('leagues')
            .select('id')
            .eq('name', 'MLB Beta Test League')
            .eq('sport', 'mlb')
            .maybeSingle();
          if (testError) console.error('[Matchups] test league query error:', testError.message);
          foundId = testLeague?.id;
        }

        if (!foundId) {
          console.error('[Matchups] no league found for user', user.id);
          setLoading(false);
          return;
        }

        setLeagueId(foundId);
        leagueIdRef.current = foundId;

        // Fetch league spin cost and user's team in parallel
        const [leagueRes, userTeamRes] = await Promise.all([
          supabase.from('leagues').select('sabotage_spin_cost, category_config, regular_season_weeks, playoff_teams').eq('id', foundId).maybeSingle(),
          supabase.from('teams').select('id').eq('league_id', foundId).eq('user_id', user.id).maybeSingle(),
        ]);
        if (leagueRes.error) console.error('[Matchups] spin cost fetch error:', leagueRes.error.message);
        if (userTeamRes.error) console.error('[Matchups] user team fetch error:', userTeamRes.error.message);
        if (leagueRes.data?.playoff_teams != null) {
          setPlayoffTeams(Number(leagueRes.data.playoff_teams));
        }
        if (leagueRes.data?.regular_season_weeks != null) {
          setRegularSeasonWeeks(Number(leagueRes.data.regular_season_weeks));
        }
        if (leagueRes.data?.sabotage_spin_cost != null) {
          setSpinCost(leagueRes.data.sabotage_spin_cost);
        }
        const catConfig = leagueRes.data?.category_config as any;
        if (catConfig?.max_spins_per_matchup != null) {
          setMaxSpinsPerMatchup(catConfig.max_spins_per_matchup);
        }
        setScoringWeights(buildScoringWeights(catConfig));
        if (catConfig?.lineup_lock_type) setLineupLockType(catConfig.lineup_lock_type);
        if (userTeamRes.data) setUserTeamId(userTeamRes.data.id);

        await fetchData(foundId);
        fetchSabotages().catch(err => console.error('[Matchups] fetchSabotages error:', err));
        setLoading(false);
      } catch (err) {
        console.error('[Matchups] init EXCEPTION (this is the Load failed source):', err);
        setLoading(false);
      }
    };
    init();
  }, [fetchData, fetchSabotages]);

  useEffect(() => {
    if (!leagueId) return;
    const interval = setInterval(() => {
      fetchData().catch(err => console.error('[Matchups] refresh error:', err));
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [leagueId, fetchData]);

  useEffect(() => {
    if (!leagueId) return;
    const channel = supabase
      .channel(`matchup-scores-${leagueId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'matchups', filter: `league_id=eq.${leagueId}` },
        () => { fetchData().catch(err => console.error('[Matchups] realtime refresh error:', err)); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [leagueId]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const openWheelModal = (matchup: MatchupRow) => {
    const opponentId = matchup.home_team_id === userTeamId ? matchup.away_team_id : matchup.home_team_id;
    setWheelMatchup(matchup);
    setWheelTargetId(opponentId);
    setSpinResult(null);
    setPendingSabotage(null);
    // Close the detail Dialog first — Radix sets pointer-events:none on body when open,
    // which blocks clicks in our portaled wheel modal
    setDetailMatchup(null);
    setWheelOpen(true);
  };

  const handleSpinResult = (option: SabotageOption) => {
    setPendingSabotage(option);
    setApplyError(null);
  };

  const handleConfirmSabotage = async () => {
    if (!pendingSabotage || !wheelMatchup || !wheelTargetId || !leagueId) return;

    const catalogEntry = mapWheelResultToCatalog(pendingSabotage);
    if (!catalogEntry) {
      logErrorToast(`"${pendingSabotage.name}" is not in the validated catalog, skipped.`);
      return;
    }

    // Self-buff cards target your own team, not the opponent
    const isSelfBuff = catalogEntry.color === 'success';
    const targetId = isSelfBuff && userTeamId ? userTeamId : wheelTargetId;

    setIsApplying(true);
    try {
      await applySabotage(wheelMatchup.id, targetId, catalogEntry);
      const cfg = RARITY_CONFIG[catalogEntry.rarity];
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-bold">{catalogEntry.icon} {catalogEntry.name} Applied!</span>
          <span className="text-xs opacity-70">{cfg.label} tier</span>
          <span className="text-sm opacity-80">{catalogEntry.description}</span>
        </div>,
        { duration: 5000 }
      );
      await betaCredits.refresh();
      // Close the modal after successful apply
      setWheelOpen(false);
      setPendingSabotage(null);
    } catch (err: any) {
      const raw = err.message || '';
      // iOS WebKit throws "Load failed" for network/CORS errors — translate to user-friendly message
      const msg = raw === 'Load failed' || raw === 'Failed to fetch'
        ? 'Connection error: check your internet and try again'
        : raw || 'Failed to apply sabotage';
      // Show only the inline error banner — don't also fire a toast (avoids double "Load failed")
      console.error('[Matchups] applySabotage error:', raw);
      setApplyError(msg);
    } finally {
      setIsApplying(false);
    }
  };

  const handleSpinAgain = () => {
    setPendingSabotage(null);
    setApplyError(null);
  };

  const handleCancelSabotage = () => {
    setPendingSabotage(null);
    setApplyError(null);
    setWheelOpen(false);
  };

  const weekMatchups = matchups.filter(m => m.week === currentWeek);
  const weekSabotages = sabotages.filter(s => s.week === currentWeek);
  const hasLive = matchups.some(m => m.status === 'live');

  // SAB-413: per-team rule is 1 Legendary per league per week. Once this team
  // has used any Legendary this week, every Legendary card is unspinnable
  // until next week — pre-filter the wheel so it never lands on one.
  const teamUsedLegendaryThisWeek = userTeamId
    ? weekSabotages.some(s =>
        s.applied_by_team_id === userTeamId &&
        MLB_SABOTAGE_OPTIONS.find(o => o.name === s.sabotage_name)?.rarity === 'legendary'
      )
    : false;
  const excludedSabotageNames = teamUsedLegendaryThisWeek
    ? MLB_SABOTAGE_OPTIONS.filter(o => o.rarity === 'legendary').map(o => o.name)
    : [];

  // Get sabotages for a specific matchup — only show if target is in this matchup
  const getSabotagesForMatchup = (matchupId: string, homeTeamId: string, awayTeamId: string) => {
    const sabs = weekSabotages.filter(s =>
      s.matchup_id === matchupId &&
      (s.target_team_id === homeTeamId || s.target_team_id === awayTeamId)
    );
    // Deduplicate by sabotage name (global sabotages may have 2 rows if both teams are targets)
    const seen = new Set<string>();
    return sabs.filter(s => {
      if (seen.has(s.sabotage_name)) return false;
      seen.add(s.sabotage_name);
      return true;
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  if (!leagueId) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="mb-2 font-medium text-foreground">No league found</p>
          <p className="text-sm">Create or join an MLB league, then generate a schedule to see matchups here.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Live indicator + last updated */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasLive && (
            <Badge className="bg-destructive text-destructive-foreground text-[10px] animate-pulse">
              ● LIVE
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[10px] text-muted-foreground">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50"
            title="Refresh scores"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <Tabs defaultValue="matchups">
        <TabsList className="bg-muted">
          <TabsTrigger value="matchups">Matchups</TabsTrigger>
          <TabsTrigger value="recap">Recap</TabsTrigger>
        </TabsList>

        <TabsContent value="matchups" className="space-y-4 mt-4">
          {/* Week selector */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold font-heading text-foreground">{weekLabel(currentWeek, regularSeasonWeeks, playoffTeams)}</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentWeek(w => Math.max(1, w - 1))}
                disabled={currentWeek <= 1}
                className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30"
              >←</button>
              <span className="text-xs text-muted-foreground min-w-[80px] text-center">
                {weekMatchups[0]?.week_start?.slice(5) || ''} – {weekMatchups[0]?.week_end?.slice(5) || ''}
              </span>
              <button
                onClick={() => setCurrentWeek(w => Math.min(maxWeek, w + 1))}
                disabled={currentWeek >= maxWeek}
                className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30"
              >→</button>
            </div>
          </div>

          {/* Matchup cards — 2-column grid, user's matchup first with highlight */}
          {(() => {
            const myMatchup = weekMatchups.find(m => m.home_team_id === userTeamId || m.away_team_id === userTeamId);
            const otherMatchups = weekMatchups.filter(m => m.id !== myMatchup?.id);
            const sortedMatchups = myMatchup ? [myMatchup, ...otherMatchups] : otherMatchups;

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {sortedMatchups.map((m) => {
                  const homeWins = m.home_score > m.away_score;
                  const awayWins = m.away_score > m.home_score;
                  const isMine = myMatchup?.id === m.id;
                  const matchupSabotages = getSabotagesForMatchup(m.id, m.home_team_id, m.away_team_id);

                  return (
                    <Card key={m.id} className={cn("bg-card border-border overflow-hidden cursor-pointer hover:border-primary/40 transition-colors", isMine && "border-orange-500/40 ring-1 ring-orange-500/20")} onClick={() => fetchMatchupDetail(m)}>
                      <CardContent className="p-0">
                        <div className="flex items-center justify-between px-2.5 py-1 bg-muted/30">
                          <div className="flex items-center gap-1.5">
                            <Badge className={cn("text-[8px] px-1.5",
                              m.status === 'live' ? 'bg-destructive text-destructive-foreground' :
                              m.status === 'final' ? 'bg-accent text-accent-foreground' :
                              'bg-muted text-muted-foreground'
                            )}>
                              {m.status.toUpperCase()}
                            </Badge>
                            {matchupSabotages.length > 0 && (
                              <Badge variant="outline" className="text-[8px] px-1.5 border-damage/50 text-damage gap-0.5">
                                <Bomb className="w-2 h-2" />
                                {matchupSabotages.length}
                              </Badge>
                            )}
                          </div>
                          {isMine && (
                            <span className="text-[8px] font-semibold text-orange-500">MY MATCHUP</span>
                          )}
                        </div>
                        <div className="flex items-center px-2.5 py-2">
                          <div className="flex-1 min-w-0">
                            <span className={cn("text-xs font-semibold line-clamp-2 break-words", awayWins ? 'text-foreground' : 'text-muted-foreground')}>
                              {m.away_team}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 px-2 shrink-0">
                            <span className={cn("text-base font-bold font-heading", awayWins ? 'text-primary' : 'text-foreground')}>
                              {m.away_score}
                            </span>
                            <span className="text-[9px] text-muted-foreground">-</span>
                            <span className={cn("text-base font-bold font-heading", homeWins ? 'text-primary' : 'text-foreground')}>
                              {m.home_score}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0 text-right">
                            <span className={cn("text-xs font-semibold line-clamp-2 break-words", homeWins ? 'text-foreground' : 'text-muted-foreground')}>
                              {m.home_team}
                            </span>
                          </div>
                        </div>

                        {matchupSabotages.length > 0 && (
                          <div className="px-2.5 pb-2 flex flex-wrap gap-1">
                            {matchupSabotages.map((sab) => (
                              <Badge
                                key={sab.id}
                                variant="outline"
                                className="text-[8px] gap-0.5 border-damage/30 text-damage/80"
                              >
                                <span>{sab.sabotage_icon}</span>
                                {sab.sabotage_name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </TabsContent>


        <TabsContent value="recap" className="mt-4">
          {leagueId && (
            <WeeklyRecap leagueId={leagueId} week={currentWeek} />
          )}
        </TabsContent>
      </Tabs>

      {/* Sabotage Wheel Full-Screen Modal — portaled to body to escape stacking contexts */}
      {wheelOpen && createPortal(
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999 }} onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-[#0F1117]/95 backdrop-blur-sm" />
          <div className="relative z-10 w-full max-w-lg mx-auto px-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close button — always visible */}
            <button
              onClick={handleCancelSabotage}
              className="absolute top-2 right-6 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-4 pt-4">
              <h2 className="text-xl font-bold text-white flex items-center justify-center gap-2">
                <Bomb className="w-5 h-5 text-orange-500" />
                Sabotage Wheel
              </h2>
              {wheelMatchup && (
                <p className="text-sm text-white/60 mt-1">
                  {wheelMatchup.away_team} vs {wheelMatchup.home_team}, Week {wheelMatchup.week}
                </p>
              )}
            </div>

            {!pendingSabotage ? (
              /* Wheel phase */
              <div className="flex justify-center">
                {(() => {
                  const mySpins = wheelMatchup ? sabotages.filter(s => s.matchup_id === wheelMatchup.id && s.applied_by_team_id === userTeamId).length : 0;
                  const spinLimitReached = maxSpinsPerMatchup !== null && mySpins >= maxSpinsPerMatchup;
                  const today = new Date();
                  const isPast = wheelMatchup && today > new Date(wheelMatchup.week_end + 'T23:59:59');
                  // SAB-412: parseISO parses bare YYYY-MM-DD as local midnight,
                  // not UTC midnight. week_start is a `date` column, so the bare
                  // `new Date(week_start)` was treating the week as starting
                  // ~5–8h early for US users (matchup flipped to "current" the
                  // prior evening instead of at local midnight).
                  const isFuture = wheelMatchup && today < parseISO(wheelMatchup.week_start);
                  const isDisabled = !wheelMatchup || !!isPast || !!isFuture || spinLimitReached;
                  const message = isPast ? 'Matchup has ended'
                    : isFuture ? 'Wheel spins when matchup begins'
                    : spinLimitReached ? `Spin limit reached (${mySpins}/${maxSpinsPerMatchup})`
                    : '';
                  return (
                    <div className="space-y-1">
                      <SabotageWheel
                        onSabotageSelected={() => {}}
                        onSpinResult={handleSpinResult}
                        deferApply
                        cost={spinCost}
                        disabled={isDisabled}
                        disabledMessage={message}
                        excludedSabotageNames={excludedSabotageNames}
                      />
                      {maxSpinsPerMatchup !== null && !isPast && !isFuture && (
                        <p className="text-center text-[10px] text-white/50">
                          Spins used: <span className="font-bold text-white">{mySpins}</span> / {maxSpinsPerMatchup}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Confirm phase — show the landed sabotage card */
              <div className="flex flex-col items-center gap-6 py-8">
                {(() => {
                  const catalogEntry = mapWheelResultToCatalog(pendingSabotage);
                  const rarity = catalogEntry?.rarity || 'common';
                  const rarityCfg = RARITY_CONFIG[rarity];
                  return (
                    <>
                      <div className={cn(
                        "w-full max-w-sm rounded-2xl p-6 text-center border-2",
                        "bg-gradient-to-b from-[#1a1d2e] to-[#0F1117]",
                        rarity === 'legendary' ? "border-yellow-500/60 shadow-[0_0_40px_rgba(234,179,8,0.2)]" :
                        rarity === 'rare' ? "border-purple-500/60 shadow-[0_0_40px_rgba(168,85,247,0.2)]" :
                        "border-orange-500/50 shadow-[0_0_40px_rgba(234,88,12,0.2)]"
                      )}>
                        <span className="text-6xl block mb-3">{pendingSabotage.icon}</span>
                        <h3 className="text-2xl font-bold text-white mb-1">{pendingSabotage.name}</h3>
                        <Badge variant="outline" className={cn('text-[10px] px-2 mb-3', rarityCfg.badgeClass)}>
                          {rarityCfg.label}
                        </Badge>
                        <p className="text-sm text-white/70 mb-4">
                          {catalogEntry?.description || pendingSabotage.description}
                        </p>
                        <div className="inline-block px-4 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/30">
                          <span className="text-sm font-bold text-orange-400">{pendingSabotage.effect}</span>
                        </div>
                      </div>

                      <div className="w-full max-w-sm space-y-2">
                        {applyError && (
                          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-center">
                            <p className="text-xs text-red-400">{applyError}</p>
                          </div>
                        )}
                        <Button
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
                          onClick={handleConfirmSabotage}
                          disabled={isApplying || !!applyError}
                        >
                          {isApplying ? (
                            <span className="flex items-center gap-2">
                              <span className="inline-block animate-spin">🎰</span>
                              Applying...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Bomb className="w-4 h-4" />
                              Apply Sabotage
                            </span>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full border-white/20 text-white hover:bg-white/10"
                          onClick={handleSpinAgain}
                        >
                          <span className="flex items-center gap-2">
                            🎯 Spin Again
                          </span>
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Matchup Detail Dialog */}
      <Dialog open={!!detailMatchup} onOpenChange={(open) => { if (!open) setDetailMatchup(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          {detailMatchup && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-heading flex items-center gap-2">
                  Week {detailMatchup.week} Matchup
                  {detailMatchup.status === 'live' && (
                    <Badge className="bg-destructive text-destructive-foreground text-[10px] animate-pulse gap-1">
                      <Activity className="w-3 h-3" /> LIVE
                    </Badge>
                  )}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-2">
                  {detailMatchup.week_start} – {detailMatchup.week_end}
                  {detailMatchup.status === 'live' && (
                    <span className="text-[10px] text-muted-foreground">• Auto-refreshing every 15s</span>
                  )}
                </DialogDescription>
              </DialogHeader>

              {/* Time period toggle */}
              <div className="flex items-center justify-center gap-1 py-1">
                {(['week', 'today'] as const).map((period) => (
                  <button
                    key={period}
                    onClick={() => setMatchupTimePeriod(period)}
                    className={cn(
                      "px-3 py-1 text-xs font-semibold rounded-full transition-colors",
                      matchupTimePeriod === period
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    )}
                  >
                    {period === 'week' ? 'Week' : 'Today'}
                  </button>
                ))}
              </div>

              {/* Score header */}
              <div className="flex items-center justify-center py-3 sm:py-4 border-b border-border gap-1 sm:gap-2">
                <div className="text-center flex-1 min-w-0">
                  <p className="text-[11px] sm:text-sm font-semibold text-foreground truncate">{detailMatchup.away_team}</p>
                  <p className={cn(
                    "text-xl sm:text-3xl font-bold font-heading mt-1 transition-all duration-500",
                    filteredAwayScore > filteredHomeScore ? "text-primary" : "text-foreground",
                    scoreFlash === 'away' && "scale-110 text-primary"
                  )}>
                    {filteredAwayScore}
                  </p>
                  {scoreFlash === 'away' && prevAwayScore !== null && (
                    <span className="text-xs text-primary animate-bounce inline-block mt-0.5">
                      +{(filteredAwayScore - prevAwayScore).toFixed(1)}
                    </span>
                  )}
                </div>
                <Badge className={`text-[10px] shrink-0 ${
                  detailMatchup.status === 'live' ? 'bg-destructive text-destructive-foreground animate-pulse' :
                  detailMatchup.status === 'final' ? 'bg-accent text-accent-foreground' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {detailMatchup.status.toUpperCase()}
                </Badge>
                <div className="text-center flex-1 min-w-0">
                  <p className="text-[11px] sm:text-sm font-semibold text-foreground truncate">{detailMatchup.home_team}</p>
                  <p className={cn(
                    "text-xl sm:text-3xl font-bold font-heading mt-1 transition-all duration-500",
                    filteredHomeScore > filteredAwayScore ? "text-primary" : "text-foreground",
                    scoreFlash === 'home' && "scale-110 text-primary"
                  )}>
                    {filteredHomeScore}
                  </p>
                  {scoreFlash === 'home' && prevHomeScore !== null && (
                    <span className="text-xs text-primary animate-bounce inline-block mt-0.5">
                      +{(filteredHomeScore - prevHomeScore).toFixed(1)}
                    </span>
                  )}
                </div>
              </div>

              {/* Active Sabotage Effects Banner — visible to everyone */}
              {(() => {
                const matchupSabs = sabotages.filter(s => s.matchup_id === detailMatchup.id && s.week === detailMatchup.week);
                if (matchupSabs.length === 0) return null;

                // De-duplicate by sabotage name for display (bilateral = one effect per matchup)
                const uniqueSabs = Array.from(
                  new Map(matchupSabs.map(s => [s.sabotage_name, s])).values()
                );

                return (
                  <div className="rounded-lg border border-damage/40 bg-damage/5 p-2 sm:p-3 space-y-2 overflow-hidden">
                    <h3 className="text-[10px] sm:text-xs font-bold text-damage flex items-center gap-1 sm:gap-1.5 uppercase tracking-wider">
                      <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                      <span className="truncate">Active Sabotages: Both Teams</span>
                    </h3>
                    <div className="space-y-1.5">
                      {uniqueSabs.map((sab) => {
                        const cfg = sab.effect_config as Record<string, any>;
                        let effectLabel = '';
                        if (sab.effect_type === 'stat_multiplier' && cfg.stat_column) {
                          const statName = (cfg.stat_column as string).replace(/_/g, ' ');
                          effectLabel = `${statName} × ${cfg.multiplier || 2}`;
                        } else if (sab.effect_type === 'score_modifier' && cfg.flat_points) {
                          effectLabel = `${cfg.flat_points > 0 ? '+' : ''}${cfg.flat_points} pts each`;
                        } else if (sab.effect_type === 'score_multiplier' && cfg.multiplier) {
                          effectLabel = `Score × ${cfg.multiplier}`;
                        } else if (sab.effect_type === 'roster_restriction') {
                          effectLabel = 'Roster effect';
                        } else if (sab.effect_type === 'info') {
                          effectLabel = 'Intel';
                        }

                        const catalogMatch = MLB_MATCHUP_SABOTAGES.find(c => c.name === sab.sabotage_name);
                        const rarity = catalogMatch?.rarity || 'common';
                        const rarityCfg = RARITY_CONFIG[rarity];

                        return (
                          <div key={sab.id} className="flex items-start gap-2 p-2 rounded-md bg-background/60 border border-border/50">
                            <span className="text-base sm:text-lg shrink-0">{sab.sabotage_icon}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-xs sm:text-sm font-semibold text-foreground">{sab.sabotage_name}</span>
                                <Badge variant="outline" className={cn('text-[8px] px-1 py-0 border', rarityCfg.badgeClass)}>
                                  {rarityCfg.label}
                                </Badge>
                                {effectLabel && (
                                  <Badge className="bg-damage/15 text-damage border-damage/30 text-[8px] sm:text-[10px] font-mono">
                                    {effectLabel}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground line-clamp-2">{sab.sabotage_description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-damage/60 italic">
                      ⚠️ All effects are double-edged. Both teams' scores are impacted equally.
                    </p>
                  </div>
                );
              })()}

              {/* Sabotage CTA button for user's own matchup */}
              {userTeamId && (detailMatchup.home_team_id === userTeamId || detailMatchup.away_team_id === userTeamId) && (() => {
                const myDetailSpins = sabotages.filter(s => s.matchup_id === detailMatchup.id && s.applied_by_team_id === userTeamId).length;
                const detailSpinLimitReached = maxSpinsPerMatchup !== null && myDetailSpins >= maxSpinsPerMatchup;
                const today = new Date();
                const detailIsPast = today > new Date(detailMatchup.week_end + 'T23:59:59');
                // SAB-412: parseISO — see comment on wheelMatchup above.
                const detailIsFuture = today < parseISO(detailMatchup.week_start);
                const noCredits = betaCredits.totalCredits < spinCost;
                const isMatchupLocked = detailMatchup.status === 'final' || detailIsPast;
                const isDisabled = isMatchupLocked || detailIsFuture || detailSpinLimitReached || noCredits;
                const disabledMessage = isMatchupLocked ? 'Matchup has ended'
                  : detailIsFuture ? 'Available when matchup begins'
                  : detailSpinLimitReached ? `Spin limit reached (${myDetailSpins}/${maxSpinsPerMatchup})`
                  : noCredits ? 'No sabotage credits available'
                  : '';

                return (
                  <div className="border-b border-border pb-4">
                    <Button
                      onClick={() => openWheelModal(detailMatchup)}
                      disabled={isDisabled}
                      className={cn(
                        "w-full py-5 text-base font-bold rounded-xl transition-all duration-300",
                        !isDisabled && "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 shadow-[0_4px_20px_rgba(234,88,12,0.3)] hover:shadow-[0_6px_28px_rgba(234,88,12,0.4)] border border-orange-400/30 text-white",
                        isDisabled && "bg-muted text-muted-foreground cursor-not-allowed"
                      )}
                    >
                      <Bomb className="w-5 h-5 mr-2" />
                      {isDisabled ? disabledMessage : `Sabotage This Matchup! ($${spinCost})`}
                    </Button>
                    {!isDisabled && (
                      <p className="text-[10px] text-muted-foreground text-center mt-2">
                        Affects both teams equally  {maxSpinsPerMatchup !== null && `(${myDetailSpins}/${maxSpinsPerMatchup} spins used)`}
                      </p>
                    )}
                  </div>
                );
              })()}

              {detailLoading ? (
                <div className="space-y-2 py-4">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}
                </div>
              ) : (
                <div className="mt-4">
                  {detailMatchup.status === 'final' && (
                    <div className="text-center mb-4">
                      <Badge className="bg-accent/20 text-accent border-accent/30 text-xs">
                        🏆 Final: {detailMatchup.away_score > detailMatchup.home_score ? detailMatchup.away_team : detailMatchup.home_score > detailMatchup.away_score ? detailMatchup.home_team : 'Tie'}
                        {detailMatchup.away_score !== detailMatchup.home_score ? ' wins!' : ''}
                      </Badge>
                      <p className="text-[10px] text-muted-foreground mt-1">Tap a player to see their scoring breakdown</p>
                    </div>
                  )}
                  <MatchupBoxScore
                    players={[]}
                    teamName=""
                    teamScore={0}
                    awayPlayers={awayPlayers.map(p => ({
                      player_id: p.player_id,
                      player_name: p.player_name,
                      position: p.position,
                      team_name: p.team_name,
                      headshot_url: p.headshot_url,
                      is_starter: p.is_starter,
                      slot: p.slot || 'BENCH',
                      stats: p.stats,
                      total_points: p.trash_points,
                      upcoming_games: p.upcoming_games,
                    }))}
                    homePlayers={homePlayers.map(p => ({
                      player_id: p.player_id,
                      player_name: p.player_name,
                      position: p.position,
                      team_name: p.team_name,
                      headshot_url: p.headshot_url,
                      is_starter: p.is_starter,
                      slot: p.slot || 'BENCH',
                      stats: p.stats,
                      total_points: p.trash_points,
                      upcoming_games: p.upcoming_games,
                    }))}
                    awayTeamName={detailMatchup.away_team}
                    homeTeamName={detailMatchup.home_team}
                    awayScore={detailMatchup.away_score}
                    homeScore={detailMatchup.home_score}
                    scoringWeights={scoringWeights}
                    probablePitcherIds={ppPlayerIds}
                    postponedPlayerIds={ppdPlayerIds}
                  />
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default MLBBetaMatchups;
